import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { custApi } from "../../lib/customerApi";
import {
  ArrowLeft, Truck, MapPin, User, Phone, Clock, CheckCircle,
  Calendar, Package, Receipt, CreditCard, CheckSquare, ShieldCheck,
} from "@phosphor-icons/react";

const STATUS_STEPS = ["pending", "in_progress", "completed"];
const STATUS_LABEL = {
  pending: "Scheduled",
  in_progress: "Out for pickup",
  completed: "Completed",
  missed: "Missed",
};
const STATUS_STYLE = {
  pending: "bg-[#F9E4B7] text-[#7A5A11]",
  in_progress: "bg-[#FEE2E2] text-[#7F1D1D]",
  completed: "bg-[#E5E2D9] text-[#5C5A55]",
  missed: "bg-[#F4C7B9] text-[#7A2E18]",
};

const PRIORITY_STYLE = {
  NORMAL: "bg-[#EFF3EE] text-[#2D5A27] border-[#C8E6C9]",
  HIGH: "bg-[#F9E4B7] text-[#7A5A11] border-[#E5DAB7]",
  URGENT: "bg-[#F4C7B9] text-[#7A2E18] border-[#F4C7B9]",
  EMERGENCY: "bg-[#F4C7B9] text-[#7A2E18] border-[#F4C7B9]",
  LOW: "bg-[#F7F6F2] text-[#8A8780] border-[#E5E2D9]",
};

const fmtDateTime = (v) => v ? new Date(v).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
const fmtDate = (v) => v ? new Date(v).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "—";

export function MeTrackingList() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    custApi.get("/me/tasks").then((r) => setTasks(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-5 px-4 py-4" data-testid="me-tracking-list">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">Tracking</div>
        <h1 className="font-heading text-xl text-[#1A1A1A]">My pickups</h1>
      </div>
      {loading && <div className="text-xs text-[#8A8780]">Loading…</div>}
      {!loading && tasks.length === 0 && (
        <div className="bg-white border border-[#E5E2D9] rounded-lg p-8 text-center">
          <Truck size={30} className="inline-block mb-3 opacity-40" />
          <div className="text-sm text-[#5C5A55]">No pickups yet</div>
          <div className="text-xs text-[#8A8780] mt-1">Your collection history will appear here.</div>
        </div>
      )}
      <div className="space-y-2">
        {tasks.map((t) => (
          <Link to={`/me/tracking/${t.id}`} key={t.id}
                className="bg-white border border-[#E5E2D9] rounded-lg p-4 block hover:border-[#DC2626] transition-colors"
                data-testid={`me-task-${t.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-[#FEE2E2] flex items-center justify-center">
                <Truck size={16} color="#DC2626" weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-[9px] uppercase tracking-[0.18em] rounded ${STATUS_STYLE[t.status] || "bg-[#E5E2D9]"}`}>
                    {STATUS_LABEL[t.status] || t.status}
                  </span>
                  <span className="font-mono text-[11px] text-[#5C5A55]">{t.date}</span>
                </div>
                <div className="text-xs text-[#8A8780] mt-1">
                  {t.assigned_staff_id ? "Staff assigned" : "Awaiting assignment"}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function MeTracking() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [paying, setPaying] = useState(false);

  const load = () => {
    custApi.get(`/me/tasks/${id}/tracking`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Unable to load"));
  };
  useEffect(() => {
    load();
    const timer = setInterval(load, 20000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (err) return (
    <div>
      <Link to="/me/requests" className="text-xs text-[#DC2626] inline-flex items-center gap-1"><ArrowLeft size={12} /> Back</Link>
      <div className="mt-4 text-sm text-[#C25D38]">{err}</div>
    </div>
  );
  if (!data) return <div className="text-sm text-[#8A8780] p-4">Loading…</div>;

  const sr = data.service_request || {};
  const task = data.task;
  const invoice = sr.invoice;
  const isPaidUpfront = sr.paid_upfront || sr.payment_status === "paid";
  const invoicePaid = invoice?.payment_status === "paid";

  // Iter 203 · Life-cycle timeline the customer will see. Each step
  // becomes done|current|pending based on the SR + task + invoice
  // state we just pulled from the tracking endpoint.
  const lifecycle = [
    { key: "booked", label: "Booked", icon: CheckSquare,
      done: true,
      at: sr.created_at, note: sr.request_no ? `Ref: ${sr.request_no}` : "" },
    { key: "scheduled", label: "Scheduled", icon: Calendar,
      done: !!task || sr.status === "SCHEDULED" || sr.status === "APPROVED"
             || sr.status === "IN_PROGRESS" || sr.status === "COMPLETED",
      // Iter 203 fix · dedupe — if scheduled_date == task.date we'd render
      // it twice ("Sat, Sep 5, 12:00 AM" + "2026-09-05"). Prefer the
      // richer scheduled_at timestamp, fall back to date-only.
      at: sr.scheduled_at || (sr.scheduled_date ? `${sr.scheduled_date}T00:00:00` : task?.date),
      note: sr.scheduled_slot || "" },
    { key: "on_the_way", label: "On the way", icon: Truck,
      done: task?.status === "in_progress" || task?.status === "completed"
             || sr.status === "COMPLETED",
      at: task?.started_at,
      note: task?.started_by_staff_name || data.staff?.name || "" },
    { key: "collected", label: "Collected", icon: CheckCircle,
      done: task?.status === "completed" || sr.status === "COMPLETED",
      at: task?.completed_at || sr.completed_at,
      note: sr.actuals?.total_weight_kg
        ? `${Number(sr.actuals.total_weight_kg).toFixed(2)} kg collected`
        : "" },
    { key: "billed", label: "Invoiced", icon: Receipt,
      done: !!invoice, at: invoice?.issued_at || invoice?.issued_date,
      note: invoice?.invoice_no ? `Bill: ${invoice.invoice_no}` : "" },
    { key: "paid", label: "Paid", icon: ShieldCheck,
      done: isPaidUpfront || invoicePaid,
      at: sr.paid_at || invoice?.paid_at,
      note: isPaidUpfront ? "Prepaid at booking" : (invoicePaid ? "Payment received" : "") },
  ];

  const openPayLink = async () => {
    if (!invoice?.public_pay_url && !invoice?.pay_url) {
      toast.error("Payment link unavailable — please contact support");
      return;
    }
    setPaying(true);
    try {
      window.location.href = invoice.public_pay_url || invoice.pay_url;
    } finally {
      // no-op; page navigates away
    }
  };

  // Iter 203 fix · plain <a href="/api/…"> can't carry the customer's
  // Bearer token so the PDF endpoint 401s. Fetch it as a blob via
  // custApi (which injects Authorization) and open the object URL.
  const downloadInvoicePDF = async () => {
    if (!invoice?.id) return;
    try {
      const r = await custApi.get(`/me/invoices/${invoice.id}/pdf`,
                                    { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      // Revoke after a short delay so the new tab has time to load.
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e?.response?.status === 404
        ? "PDF not ready yet — please try again in a moment"
        : "Unable to download invoice");
    }
  };

  return (
    <div className="space-y-4 pb-6" data-testid="me-tracking-detail">
      <Link to="/me/requests" className="text-xs text-[#DC2626] inline-flex items-center gap-1">
        <ArrowLeft size={12} /> All requests
      </Link>

      {/* -------------------- HEADER CARD -------------------- */}
      <div className="rounded-2xl bg-gradient-to-br from-[#7F1D1D] to-[#DC2626] text-white p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/70">Request</div>
            <div className="font-heading text-xl font-bold tracking-tight" data-testid="mt-request-no">
              {sr.request_no || (task?.date && `Pickup ${task.date}`)}
            </div>
            <div className="mt-1 flex flex-wrap gap-1.5 items-center">
              {sr.priority && (
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/20 border border-white/25">
                  {sr.priority}
                </span>
              )}
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/20 border border-white/25">
                {sr.status || task?.status?.toUpperCase() || "PENDING"}
              </span>
              {isPaidUpfront && (
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#C8E6C9] text-[#1B5E20] font-semibold flex items-center gap-1"
                       data-testid="mt-paid-upfront">
                  <CheckCircle size={10} weight="fill"/>Paid
                </span>
              )}
            </div>
          </div>
          {sr.payable_total != null && (
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/70">Amount</div>
              <div className="font-heading text-2xl font-bold" data-testid="mt-payable-total">
                ₹{Number(sr.payable_total).toFixed(0)}
              </div>
              {(sr.extra_charges_total || 0) > 0 && (
                <div className="text-[10px] text-white/70">incl. ₹{Number(sr.extra_charges_total).toFixed(0)} add-on</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* -------------------- LIFECYCLE TIMELINE -------------------- */}
      <div className="bg-white border border-[#E5E2D9] rounded-2xl p-4" data-testid="mt-timeline">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780] mb-3">Progress</div>
        <ol className="relative">
          {lifecycle.map((step, i) => {
            const IconEl = step.icon;
            const last = i === lifecycle.length - 1;
            return (
              <li key={step.key} className="flex gap-3 pb-3" data-testid={`mt-step-${step.key}`}>
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                    step.done
                      ? "bg-[#DC2626] border-[#DC2626] text-white"
                      : "bg-white border-[#E5E2D9] text-[#8A8780]"
                  }`}>
                    <IconEl size={14} weight={step.done ? "fill" : "regular"}/>
                  </div>
                  {!last && (
                    <div className={`flex-1 w-0.5 mt-1 min-h-[16px] ${step.done ? "bg-[#DC2626]" : "bg-[#E5E2D9]"}`}/>
                  )}
                </div>
                <div className="flex-1 min-w-0 pb-2">
                  <div className={`text-sm font-medium ${step.done ? "text-[#1A1A1A]" : "text-[#8A8780]"}`}>
                    {step.label}
                  </div>
                  {step.at && (
                    <div className="text-[11px] text-[#5C5A55] mt-0.5">{fmtDateTime(step.at)}</div>
                  )}
                  {step.note && (
                    <div className="text-[11px] text-[#8A8780] mt-0.5">{step.note}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* -------------------- PACKAGE + PAYABLE -------------------- */}
      {sr.package_name && (
        <div className="bg-white border border-[#E5E2D9] rounded-2xl p-4" data-testid="mt-package">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780] mb-2">Your package</div>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: sr.package_colour || "#FEE2E2" }}>
                <Package size={16} color="#7F1D1D" weight="duotone"/>
              </div>
              <div className="min-w-0">
                <div className="font-heading text-base text-[#1A1A1A]" data-testid="mt-package-name">
                  {sr.package_name}
                </div>
                {sr.package_billing_cycle && (
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780] mt-0.5">
                    {String(sr.package_billing_cycle).replace(/_/g, " ")}
                  </div>
                )}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780]">Base</div>
              <div className="font-mono text-base font-semibold text-[#1A1A1A]">
                ₹{Number(sr.package_price || 0).toFixed(0)}
              </div>
            </div>
          </div>
          {(sr.extra_charges || []).length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#E5E2D9] space-y-1">
              {sr.extra_charges.map((ec) => (
                <div key={ec.id} className="flex items-center justify-between text-xs">
                  <span className="text-[#5C5A55]">
                    {ec.label}
                    {ec.priority && (
                      <span className="ml-1 text-[9px] text-[#8A8780] uppercase tracking-wider">
                        · auto
                      </span>
                    )}
                  </span>
                  <span className="font-mono text-[#7A2E18]">+₹{Number(ec.amount).toFixed(2)}</span>
                </div>
              ))}
              <div className="pt-1 mt-1 border-t border-dashed border-[#E5E2D9] flex items-center justify-between text-sm font-semibold">
                <span>Total payable</span>
                <span className="font-mono">₹{Number(sr.payable_total || 0).toFixed(2)}</span>
              </div>
            </div>
          )}
          {sr.preferred_date && (
            <div className="mt-3 pt-3 border-t border-[#E5E2D9] flex items-center gap-2 text-xs text-[#5C5A55]">
              <Calendar size={12}/> Preferred date: <strong className="text-[#1A1A1A]">{fmtDate(sr.preferred_date)}</strong>
              {sr.preferred_slot && <span> · {sr.preferred_slot}</span>}
            </div>
          )}
          {sr.address && (
            <div className="mt-1 flex items-start gap-2 text-xs text-[#5C5A55]">
              <MapPin size={12} className="mt-0.5 shrink-0"/> <span className="whitespace-pre-wrap">{sr.address}</span>
            </div>
          )}
        </div>
      )}

      {/* -------------------- ASSIGNMENT / TEAM -------------------- */}
      {(data.staff || data.vehicle || task) && (
        <div className="bg-white border border-[#E5E2D9] rounded-2xl p-4" data-testid="mt-assignment">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780] mb-3">Assignment</div>
          {data.staff && (
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-full bg-[#F7F6F2] border border-[#E5E2D9] flex items-center justify-center">
                <User size={18} color="#DC2626" weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-base text-[#1A1A1A]" data-testid="mt-staff-name">
                  {data.staff.name}
                </div>
                <div className="text-xs text-[#5C5A55] capitalize">
                  {data.staff.role?.replace(/_/g, " ") || "Field staff"}
                </div>
                {data.staff.phone && (
                  <a href={`tel:${data.staff.phone}`}
                      data-testid="mt-staff-call"
                      className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#DC2626] font-medium">
                    <Phone size={12} weight="fill" /> {data.staff.phone}
                  </a>
                )}
              </div>
            </div>
          )}
          {data.vehicle && (
            <div className={`${data.staff ? "mt-3 pt-3 border-t border-[#E5E2D9]" : ""} flex items-start gap-3`}>
              <div className="w-11 h-11 rounded-full bg-[#F7F6F2] border border-[#E5E2D9] flex items-center justify-center">
                <Truck size={18} color="#DC2626" weight="duotone"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-base text-[#1A1A1A]" data-testid="mt-vehicle-no">
                  {data.vehicle.vehicle_no || data.vehicle.registration_no || "Vehicle assigned"}
                </div>
                <div className="text-xs text-[#5C5A55]">
                  {data.vehicle.type && <span className="capitalize">{data.vehicle.type.replace(/_/g, " ")}</span>}
                  {data.vehicle.capacity_kg && <span> · {data.vehicle.capacity_kg}kg</span>}
                  {data.vehicle.model && <span> · {data.vehicle.model}</span>}
                </div>
              </div>
            </div>
          )}
          {(data.assignment_terminal || data.eta_minutes != null || data.distance_km != null) && (
            <div className="mt-3 pt-3 border-t border-[#E5E2D9]">
              {data.assignment_terminal ? (
                <div className="flex items-center gap-2 text-xs" data-testid="mt-assignment-completed">
                  <div className="w-6 h-6 rounded-full bg-[#EFF3EE] flex items-center justify-center">
                    <CheckCircle size={13} weight="fill" className="text-[#1B5E20]"/>
                  </div>
                  <span className="font-semibold text-[#1B5E20]">Assignment completed</span>
                  <span className="text-[#8A8780] text-[10px]">
                    · service done{invoice ? " · billed" : ""}
                  </span>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">Distance</div>
                    <div className="font-heading text-xl" data-testid="mt-distance">
                      {data.distance_km != null ? `${data.distance_km} km` : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">ETA</div>
                    <div className="font-heading text-xl text-[#DC2626]" data-testid="mt-eta">
                      {data.eta_minutes != null ? `~${data.eta_minutes} min` : "—"}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Iter 204 · Pre-navigate placeholder: staff assigned but
                collector hasn't tapped Navigate yet, so no live tracking
                is shown. */}
          {!data.assignment_terminal && !data.navigation_started_at && data.staff && (
            <div className="mt-3 pt-3 border-t border-[#E5E2D9] flex items-center gap-2 text-xs text-[#7A5A11] bg-[#FEF3C7] border border-[#F9E4B7] rounded-md p-2"
                  data-testid="mt-assignment-waiting-nav">
              <Clock size={12} weight="fill"/>
              Field staff assigned. Live tracking will start when the
              collector heads to your location.
            </div>
          )}
          {data.staff_location && !data.assignment_terminal && (
            <div className="mt-3 flex items-center gap-2 text-[11px] text-[#8A8780]">
              <MapPin size={12} /> Last update {new Date(data.staff_location.updated_at).toLocaleTimeString()}
              <Clock size={12} className="ml-1" /> auto-refresh 20s
            </div>
          )}
        </div>
      )}

      {/* -------------------- INVOICE -------------------- */}
      {invoice && (
        <div className="bg-white border border-[#E5E2D9] rounded-2xl p-4" data-testid="mt-invoice">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">Invoice</div>
            {invoice.status && (
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                invoicePaid ? "bg-[#C8E6C9] text-[#1B5E20]" : "bg-[#F9E4B7] text-[#7A5A11]"
              }`} data-testid="mt-invoice-status">
                {String(invoice.status).replace(/_/g, " ")}
              </span>
            )}
          </div>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-heading text-base text-[#1A1A1A]" data-testid="mt-invoice-no">
                {invoice.invoice_no}
              </div>
              {invoice.issued_at && (
                <div className="text-xs text-[#5C5A55] mt-0.5">Issued {fmtDateTime(invoice.issued_at)}</div>
              )}
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780]">Total</div>
              <div className="font-heading text-lg font-bold" data-testid="mt-invoice-total">
                ₹{Number(invoice.total || 0).toFixed(2)}
              </div>
            </div>
          </div>
          {invoice.excess_total > 0 && (
            <div className="mt-3 pt-3 border-t border-dashed border-[#E5E2D9]">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#7A2E18] mb-1">Extra-quantity charges</div>
              <div className="flex items-center justify-between text-xs">
                <span>{Number(invoice.excess_kg || 0).toFixed(2)} kg over allowance</span>
                <span className="font-mono text-[#7A2E18]">+₹{Number(invoice.excess_total).toFixed(2)}</span>
              </div>
            </div>
          )}
          <div className="mt-3 flex gap-2 flex-wrap">
            <button onClick={downloadInvoicePDF}
                     data-testid="mt-invoice-pdf"
                     className="h-9 px-3 border border-[#DC2626] text-[#DC2626] rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#FEE2E2]">
              <Receipt size={12}/> Download PDF
            </button>
            {!invoicePaid && (invoice.public_pay_url || invoice.pay_url) && (
              <button onClick={openPayLink} disabled={paying}
                       data-testid="mt-invoice-pay"
                       className="h-9 px-3 bg-[#0B2B66] hover:bg-[#081E4A] disabled:opacity-40 text-white rounded-md text-xs inline-flex items-center gap-1.5 font-medium">
                <CreditCard size={12} weight="fill"/> {paying ? "Redirecting…" : "Pay now"}
              </button>
            )}
            {invoicePaid && invoice.paid_at && (
              <span data-testid="mt-invoice-paid-at"
                     className="h-9 px-3 bg-[#EFF3EE] text-[#1B5E20] border border-[#C8E6C9] rounded-md text-xs inline-flex items-center gap-1.5">
                <CheckCircle size={12} weight="fill"/> Paid on {fmtDate(invoice.paid_at)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* -------------------- PAID UPFRONT (pre-invoice) -------------------- */}
      {isPaidUpfront && !invoice && (
        <div className="bg-[#EFF3EE] border border-[#C8E6C9] rounded-2xl p-4" data-testid="mt-prepaid-notice">
          <div className="flex items-start gap-3">
            <ShieldCheck size={18} weight="fill" className="text-[#1B5E20] mt-0.5"/>
            <div className="flex-1">
              <div className="font-heading text-sm text-[#1B5E20]">Payment received — slot confirmed</div>
              <div className="text-xs text-[#2D5A27] mt-1">
                You paid ₹{Number(sr.payable_total || 0).toFixed(0)} on {fmtDateTime(sr.paid_at)}. Your invoice will be issued after service is completed.
              </div>
              {sr.razorpay_payment_id && (
                <div className="text-[10px] text-[#5C5A55] mt-1 font-mono">
                  Razorpay ref: {sr.razorpay_payment_id}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* -------------------- PENDING PLACEHOLDER -------------------- */}
      {(data.pending_scheduling || !task) && !data.staff && (
        <div className="bg-[#F9E4B7]/40 border border-[#F9E4B7] rounded-2xl p-4"
              data-testid="mt-pending-notice">
          <div className="flex items-start gap-3">
            <Clock size={18} weight="fill" className="text-[#B45309] mt-0.5"/>
            <div className="flex-1">
              <div className="font-heading text-sm text-[#7A5A11]">Awaiting scheduling</div>
              <div className="text-xs text-[#7A5A11] mt-1">
                {data.message || "Your request is pending review. You'll see the assigned staff, vehicle and live ETA here once scheduling is done."}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="text-[11px] text-center text-[#8A8780] italic">
        This page auto-refreshes every 20 seconds.
      </div>
    </div>
  );
}
